import {
  Controller,
  type Control,
  type UseFormSetValue,
  type UseFormTrigger,
} from "react-hook-form";
import type { StringsShape } from "../../utils/constants";
import type { OrgConfig, VisitorTypeField } from "../../types/orgConfig";
import Select from "react-select";
import type { VisitorFormData } from "./visitorForm";
import HostCombobox from "../../components/common/HostDropdown/HostCombobox";
import type { Host } from "../../components/common/HostDropdown/hostSelector";
import { touchlessService } from "../../services/api/touchlessService";
import { Form } from "react-bootstrap";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

const renderFields = (
  field: VisitorTypeField,
  index: number,
  control: Control<VisitorFormData, unknown>,
  watchedValues: VisitorFormData,
  setValue: UseFormSetValue<VisitorFormData>,
  trigger: UseFormTrigger<VisitorFormData>,
  onFocusInput: (index: number, fieldName?: string) => void,
  onOutSideClickInputArea: () => void,
  btnColor: string,
  dropdownOptionsForSonyFields: {
    [key: string]: Array<{
      label: string;
      value: string;
      availableSpots?: string;
      totalSpots?: string;
    }>;
  },
  advanceLocationFields: string[],
  notNeededFeature: boolean,
  PARKING_LOT: string,
  getDropdownOptions: (fid: string) => Array<{
    label: string;
    value: string;
    availableSpots?: string;
    totalSpots?: string;
  }>,
  orgConfig: OrgConfig,
  groupId: string | null | undefined,
  S: StringsShape,
) => {
  const fieldId = `input-${index}`;
  const fieldName = field.fid || field.id || field.name || `field-${index}`;
  const isRequired = field.status === "ACTIVE" && field.setting === "MANDATORY";
  const requiredMessage = (): string =>
    S.VALIDATION.FIELD_REQUIRED_TEMPLATE.replace(
      "{field}",
      field.displayText || field.name || "",
    );
  if (field.type == "REFERENCE" || field.type == "DROPDOWN") {
    const options = () => {
      if (
        field.fid !== undefined &&
        advanceLocationFields.includes(field.fid)
      ) {
        return getDropdownOptions(field.fid).map((item) => ({
          label: item.label,
          value: item.value,
        }));
      } else {
        return field.options || [];
      }
    };
    return field.fid !== "HOST" && field.type !== "REFERENCE" ? (
      <>
        <Controller
          name={fieldName}
          control={control}
          rules={{
            required: isRequired ? requiredMessage() : false,
          }}
          render={({ field: { onChange, value }, fieldState: { error } }) => (
            <>
              <Select
                id={fieldId}
                options={options()}
                isSearchable={false}
                value={options().find((option) => option.value === value)}
                onChange={(selectedOption) => onChange(selectedOption?.value)}
                placeholder={S.VISITOR_FORM.SELECT_FIELD_PLACEHOLDER.replace("{field}", field.displayText || field.name || "")}
                classNames={{
                  control: () => "form-control--select",
                }}
                menuPortalTarget={document.body}
                menuPosition="absolute"
                styles={{
                  control: (base, state) => ({
                    ...base,
                    border: "none",
                    outline: "none",
                    boxShadow: state.isFocused
                      ? "0px 0px 5px 1px var(--primary-btn-color-muted)"
                      : "none",
                  }),
                  singleValue: (base) => ({
                    ...base,
                    fontSize: "var(--font-size-sm)",
                  }),
                  placeholder: (base) => ({
                    ...base,
                    fontSize: "var(--font-size-sm)",
                  }),
                  option: (base) => ({
                    ...base,
                    fontSize: "var(--font-size-sm)",
                  }),
                  menuPortal: (base) => ({
                    ...base,
                    zIndex: 9999,
                  }),
                  menu: (base) => ({
                    ...base,
                    zIndex: 9999,
                  }),
                }}
                onFocus={() => onFocusInput(index, fieldName)}
                onBlur={() => onOutSideClickInputArea()}
                onMenuOpen={() => trigger(fieldName)}
                required={isRequired}
                data-testid={`VF-input-field-${fieldId}`}
              />
              {error && (
                <div
                  className="text-danger"
                  data-testid={`VF-input-field-error-${fieldId}`}
                  style={{ fontSize: "0.875em", marginTop: "0.25rem" }}
                >
                  {error.message}
                </div>
              )}
              {notNeededFeature &&
                (() => {
                  const selectedParkingLot =
                    field.name === PARKING_LOT
                      ? dropdownOptionsForSonyFields[PARKING_LOT].find(
                          (option) => option.value === value,
                        )
                      : null;
                  return (
                    selectedParkingLot?.availableSpots !== undefined && (
                      <div className="parking-info">
                        <span className="parking-info-box">
                          {selectedParkingLot.availableSpots} Available{" "}
                        </span>
                        <span className="parking-info-box">
                          {selectedParkingLot.totalSpots} Total Stalls
                        </span>
                      </div>
                    )
                  );
                })}
            </>
          )}
        />
      </>
    ) : (
      <Controller
        name="hostUserId"
        control={control}
        rules={{
          required: isRequired ? S.VALIDATION.HOST_REQUIRED : false,
        }}
        render={({ fieldState: { error } }) => (
          <>
            <HostCombobox
              selectedHostId={watchedValues.hostUserId || ""}
              selectedHostName={watchedValues.hostName || ""}
              onHostSelect={(hostId, firstName, lastName) => {
                setValue("hostUserId", hostId);
                setValue("hostName", `${firstName} ${lastName}`);
                trigger("hostUserId");
              }}
              onClearHost={() => {
                setValue("hostUserId", "");
                setValue("hostName", "");
                trigger("hostUserId");
              }}
              loadHostOptions={async (searchTerm) => {
                const results = await touchlessService.getUsersDetailsByName(
                  searchTerm,
                  orgConfig,
                  groupId,
                );
                return results.map((host: Host) => ({
                  value: host.id,
                  label: `${host.firstName} ${host.lastName}`,
                  firstName: host.firstName,
                  lastName: host.lastName,
                  title: host.title,
                  department: host.department,
                }));
              }}
              btnColor={btnColor}
              fieldId={fieldId}
              className="host-combobox--form"
              dataTestId={`VF-input-field-${fieldId}`}
            />
            {error && (
              <div
                className="text-danger"
                style={{
                  fontSize: "0.875em",
                  marginTop: "0.25rem",
                  display: "block",
                  width: "100%",
                }}
                data-testid={`VF-input-field-error-${fieldId}`}
              >
                {error.message}
              </div>
            )}
          </>
        )}
      />
    );
  }

  if (
    field.fid !== "HOST" &&
    field.status === "ACTIVE" &&
    field.orgCustomFieldId &&
    field.type === "RADIO"
  ) {
    return (
      <Controller
        name={fieldName}
        control={control}
        rules={{
          required: isRequired ? requiredMessage() : false,
        }}
        render={({ field: controllerField, fieldState: { error } }) => (
          <div>
            {field.options?.map((option, idx) => (
              <Form.Check
                key={idx}
                type="radio"
                id={`${fieldId}-${idx}`}
                name={controllerField.name}
                label={option.label}
                value={option.value}
                checked={controllerField.value === option.value}
                onChange={(e) => controllerField.onChange(e.target.value)}
                onFocus={() => onFocusInput(index, fieldName)}
                onBlur={() => onOutSideClickInputArea()}
                onClick={() => trigger(fieldName)}
                // isInvalid={!!error}
                required={isRequired}
                data-testid={`VF-input-field-${fieldId}`}
              />
            ))}
            {error && (
              <span
                // type="invalid"
                className="text-danger"
                style={{ fontSize: "0.875em", marginTop: "0.25rem" }}
                data-testid={`VF-input-field-error-${fieldId}`}
              >
                {error.message}
              </span>
            )}
          </div>
        )}
      />
    );
  }

  if (
    field.fid !== "HOST" &&
    field.status === "ACTIVE" &&
    field.orgCustomFieldId &&
    field.type === "DATEPICKER"
  ) {
    return (
      <Controller
        name={fieldName}
        control={control}
        rules={{
          required: isRequired ? requiredMessage() : false,
        }}
        render={({ field: controllerField, fieldState: { error } }) => {
          const rawValue = ((controllerField.value as string) || "").split("T")[0];
          const selectedDate = rawValue ? new Date(`${rawValue}T00:00:00`) : null;
          return (
            <div>
              <DatePicker
                id={fieldId}
                selected={selectedDate}
                onChange={(date: Date | null) =>
                  controllerField.onChange(date ? date.toISOString().split("T")[0] : "")
                }
                dateFormat="EEE, d MMM yyyy"
                minDate={new Date()}
                placeholderText={S.VISITOR_FORM.SELECT_FIELD_PLACEHOLDER.replace("{field}", field.displayText || field.name || "")}
                className="form-control"
                wrapperClassName="w-100"
                required={isRequired}
                data-testid={`VF-input-field-${fieldId}`}
              />
              {error && (
                <span
                  className="text-danger"
                  style={{ fontSize: "0.875em", marginTop: "0.25rem" }}
                  data-testid={`VF-input-field-error-${fieldId}`}
                >
                  {error.message}
                </span>
              )}
            </div>
          );
        }}
      />
    );
  }

  return (
    <>
      <Controller
        name={fieldName}
        control={control}
        rules={{
          required: isRequired ? requiredMessage() : false,
        }}
        render={({ field: { onChange, value }, fieldState: { error } }) => (
          <div>
            <Form.Control
              id={fieldId}
              type={
                field.type === "EMAIL"
                  ? "email"
                  : field.type === "NUMBER"
                    ? "number"
                    : "text"
              }
              value={(value as string) || ""}
              onChange={(e) => {
                const val = (e.target as HTMLInputElement).value;
                const isTextField = field.type !== "EMAIL" && field.type !== "NUMBER";
                onChange(isTextField ? val.slice(0, 200) : e);
              }}
              onFocus={() => onFocusInput(index)}
              onBlur={() => onOutSideClickInputArea()}
              required={isRequired}
              onClick={() => trigger(fieldName)}
              maxLength={field.type !== "EMAIL" && field.type !== "NUMBER" ? 200 : undefined}
              data-testid={`VF-input-field-${fieldId}`}
            />
            <span
              // type="invalid"
              className="text-danger"
              style={{ fontSize: "0.875em", marginTop: "0.25rem" }}
              data-testid={`VF-input-field-error-${fieldId}`}
            >
              {error?.message}
            </span>
          </div>
        )}
      />
    </>
  );
};

export default renderFields;
